import { useNavigate } from '@fukuri-admins/hooks/common';
import { useAlertDialog } from '@fukuri-admins/ui/common/AlertDialog';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { useSearchParams } from 'react-router-dom';
import { ROUTES, replaceRouteParams } from '~/utils/routes';
import { InternalNewsListPresentation } from '../presentations';
import { useDeleteInternalNews } from './useDeleteInternalNews';
import { useGetInternalNewsList } from './useGetInternalNewsList';

type PresentationProps = React.ComponentProps<typeof InternalNewsListPresentation>;

type IUseInternalNewsList = () => {
  state: {
    data: ReturnType<typeof useGetInternalNewsList>['data'];
    currentPage: number;
    totalPage: number;
    totalCount: number;
  };
  actions: {
    handleEditClick: PresentationProps['onEdit'];
    handleDeleteClick: PresentationProps['onDelete'];
    handleDetailClick: PresentationProps['onDetailClick'];
    handlePageChange: PresentationProps['pager']['onPageChange'];
  };
};

/**
 * 社内報一覧の状態管理とアクションを提供するコンポーネントフック
 */
export const useInternalNewsList: IUseInternalNewsList = () => {
  const threeLetterCode = getThreeLetterCode();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentPage = Number(searchParams.get('page')) || 1;
  const page = String(currentPage);
  const { showAlertDialog } = useAlertDialog();
  const { showToast } = useToast();

  const { data, totalPage, totalCount, mutate } = useGetInternalNewsList({ threeLetterCode, page });

  const { trigger: deleteInternalNews } = useDeleteInternalNews(threeLetterCode, {
    onSuccess: () => {
      showToast({
        message: '社内報を削除しました。',
        status: 'success',
      });
      mutate();
    },
    onError: () => {
      showToast({
        message: '削除に失敗しました。',
        status: 'danger',
      });
    },
  });

  /**
   * 行クリック時のハンドラ。詳細画面に遷移する。
   *
   * @param id - 詳細表示対象の社内報 ID
   */
  const handleDetailClick = (id: number) => {
    navigate(replaceRouteParams(ROUTES.INTERNAL_NEWS.DETAIL, { id }));
  };

  /**
   * 編集ボタンクリック時のハンドラ
   *
   * @param id - 編集対象の社内報 ID
   */
  const handleEditClick = (id: number) => {
    navigate(replaceRouteParams(ROUTES.INTERNAL_NEWS.EDIT, { id }));
  };

  /**
   * 削除ボタンクリック時のハンドラ。AlertDialog 経由で確認後に削除 API を実行する。
   *
   * @param id - 削除対象の社内報 ID
   */
  const handleDeleteClick = (id: number) => {
    const newsletter = data.client_newsletters.find(n => n.id === id);
    if (!newsletter) return;

    showAlertDialog({
      title: `「${newsletter.title}」を削除しますか？`,
      message: '削除すると元に戻すことはできません。',
      confirmText: '削除',
      confirmButtonVariant: 'destructive',
      onConfirm: async () => {
        await deleteInternalNews(id);
      },
    });
  };

  /**
   * ページネーションのページを変更する
   */
  const handlePageChange: PresentationProps['pager']['onPageChange'] = newPage => {
    setSearchParams(prev => {
      prev.set('page', String(newPage));
      return prev;
    });
  };

  return {
    state: {
      data,
      currentPage,
      totalPage,
      totalCount,
    },
    actions: {
      handleEditClick,
      handleDeleteClick,
      handleDetailClick,
      handlePageChange,
    },
  };
};
